import React from 'react';
import {
  Alert,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Table,
} from 'antd';
import {
  CarOutlined,
  FileTextOutlined,
  InboxOutlined,
  ShoppingOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dateUtils';
import { formatUnit } from '../../../utils/units';
import { formatLocalizedPercent } from '../../../utils/numberFormatting';
import { OrderMarginExplanationDto } from '../api/ordersApi';
import { getOrderProductDisplayName } from '../orderProductDisplay';
import {
  renderSectionTitle,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_HEAD_STYLE,
  SECTION_CARD_STYLE,
} from '../orderDetailLayout';

interface OrderMarginExplanationSectionProps {
  marginExplanation: OrderMarginExplanationDto | null;
  loading: boolean;
}

export const OrderMarginExplanationSection: React.FC<OrderMarginExplanationSectionProps> = ({
  marginExplanation,
  loading,
}) => {
  const { t, i18n } = useTranslation(['orders']);
  const lines = marginExplanation?.lines || [];
  const unassignedAllocations = marginExplanation?.unassignedAllocations || [];
  const supplierPurchaseCostPLN = lines.reduce((sum, line) => sum + Number(line.supplierPurchaseCostPLN || 0), 0)
    + unassignedAllocations.reduce((sum, allocation) => sum + Number(allocation.effectiveAllocatedPurchaseCostPLN || 0), 0);
  const supplierLandedCostPLN = lines.reduce((sum, line) => sum + Number(line.supplierLandedCostPLN || 0), 0)
    + unassignedAllocations.reduce((sum, allocation) => sum + Number(allocation.effectiveAllocatedLandedCostPLN || 0), 0);
  const costBuildUpRows = marginExplanation ? [
    {
      key: 'supplierPurchaseCost',
      label: t('orders:orderDetail.supplierPurchaseCostCategory'),
      description: t('orders:orderDetail.supplierPurchaseCostCategoryDescription'),
      value: supplierPurchaseCostPLN,
      icon: <ShoppingOutlined />,
      color: '#2563eb',
      background: '#eff6ff',
    },
    {
      key: 'supplierLandedCost',
      label: t('orders:orderDetail.supplierLandedCostCategory'),
      description: t('orders:orderDetail.supplierLandedCostCategoryDescription'),
      value: supplierLandedCostPLN,
      icon: <CarOutlined />,
      color: '#c2410c',
      background: '#fff7ed',
    },
    {
      key: 'warehouseCost',
      label: t('orders:orderDetail.warehouseCostCategory'),
      description: t('orders:orderDetail.warehouseCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.cogsPLN,
      icon: <InboxOutlined />,
      color: '#0f766e',
      background: '#f0fdfa',
    },
    {
      key: 'transportCost',
      label: t('orders:orderDetail.transportCostCategory'),
      description: t('orders:orderDetail.transportCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.transportCostPLN,
      icon: <CarOutlined />,
      color: '#7c3aed',
      background: '#f5f3ff',
    },
    {
      key: 'otherOrderCosts',
      label: t('orders:orderDetail.otherOrderCostsCategory'),
      description: t('orders:orderDetail.otherOrderCostsCategoryDescription'),
      value: marginExplanation.orderLevelCosts.manualCustomerCostPLN,
      icon: <FileTextOutlined />,
      color: '#475569',
      background: '#f8fafc',
    },
    {
      key: 'productFallbackCost',
      label: t('orders:orderDetail.productFallbackCostCategory'),
      description: t('orders:orderDetail.productFallbackCostCategoryDescription'),
      value: marginExplanation.orderLevelCosts.productFallbackCostPLN,
      icon: <FileTextOutlined />,
      color: '#64748b',
      background: '#f8fafc',
    },
  ].filter((row) => Number(row.value || 0) !== 0) : [];
  const exceptionRows = marginExplanation ? [
    {
      key: 'unassignedAllocationCost',
      label: t('orders:orderDetail.unassignedAllocationCost'),
      value: marginExplanation.totals.unassignedAllocationCostPLN,
    },
    {
      key: 'unexplainedCost',
      label: t('orders:orderDetail.unexplainedCost'),
      value: marginExplanation.orderLevelCosts.unexplainedCostPLN,
    },
  ].filter((row) => Number(row.value || 0) !== 0) : [];
  const hasCostBuildUp = costBuildUpRows.length > 0 || exceptionRows.length > 0;

  return (
    <Card
      title={renderSectionTitle(t('orders:orderDetail.marginExplanation'))}
      style={SECTION_CARD_STYLE}
      headStyle={SECTION_CARD_HEAD_STYLE}
      bodyStyle={SECTION_CARD_BODY_STYLE}
      loading={loading}
    >
      {marginExplanation ? (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {marginExplanation.order.exchangeRate && (
            <Alert
              type="info"
              showIcon
              message={t('orders:orderDetail.exchangeRateSnapshot')}
              description={[
                `${marginExplanation.order.currency} -> PLN: ${marginExplanation.order.exchangeRate}`,
                marginExplanation.order.exchangeRateEffectiveDate
                  ? `${t('orders:orderDetail.rateFrom')} ${formatDate(marginExplanation.order.exchangeRateEffectiveDate)}`
                  : null,
                marginExplanation.order.exchangeRateSource || null,
                marginExplanation.order.exchangeRateTableNumber || null,
              ].filter(Boolean).join(' · ')}
            />
          )}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={4}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('orders:orderDetail.revenue')}</div>
              <strong>{formatCurrency(marginExplanation.totals.revenuePLN, 'PLN')}</strong>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('orders:orderDetail.caseAPurchaseCost')}</div>
              <strong>{formatCurrency(supplierPurchaseCostPLN, 'PLN')}</strong>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('orders:orderDetail.caseALandedCost')}</div>
              <strong>{formatCurrency(supplierLandedCostPLN, 'PLN')}</strong>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('orders:orderDetail.totalCost')}</div>
              <strong>{formatCurrency(marginExplanation.totals.totalCostPLN, 'PLN')}</strong>
            </Col>
            <Col xs={24} sm={12} lg={4}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>{t('orders:orderDetail.margin')}</div>
              <strong>{formatCurrency(marginExplanation.totals.marginPLN, 'PLN')} ({formatLocalizedPercent(marginExplanation.totals.marginPercent, i18n.language)})</strong>
            </Col>
          </Row>
          {!marginExplanation.totals.reconciles && (
            <Alert
              type="warning"
              showIcon
              message={t('orders:orderDetail.marginExplanationUnreconciled')}
            />
          )}
          <Table
            dataSource={marginExplanation.lines}
            rowKey="id"
            size="middle"
            pagination={false}
            locale={{ emptyText: t('orders:orderDetail.noMarginExplanationLines') }}
            expandable={{
              rowExpandable: (record) => record.allocations.length > 0,
              expandedRowRender: (record) => (
                <Table
                  dataSource={record.allocations}
                  rowKey={(allocation) => allocation.linkId || `${allocation.supplierOrderId}-${allocation.supplierOrderItemId}`}
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: t('orders:supplierOrders.table.supplierOrderNumber'),
                      dataIndex: 'supplierOrderNumber',
                      key: 'supplierOrderNumber',
                    },
                    {
                      title: t('orders:supplierOrders.table.supplier'),
                      dataIndex: 'supplierName',
                      key: 'supplierName',
                      render: (value: string | null) => value || '-',
                    },
                    {
                      title: t('orders:supplierOrders.form.supplierOrderItem'),
                      dataIndex: 'supplierOrderItemName',
                      key: 'supplierOrderItemName',
                      render: (value: string | null) => value || '-',
                    },
                    {
                      title: t('orders:supplierOrders.form.allocatedQuantity'),
                      key: 'allocatedQuantity',
                      align: 'right' as const,
                      render: (_value, allocation) => (
                        allocation.allocatedQuantity == null
                          ? '-'
                          : `${allocation.allocatedQuantity.toLocaleString('pl-PL')} ${allocation.allocatedUnit ? formatUnit(allocation.allocatedUnit) : ''}`.trim()
                      ),
                    },
                    {
                      title: t('orders:orderDetail.purchaseCost'),
                      dataIndex: 'effectiveAllocatedPurchaseCostPLN',
                      key: 'purchaseCost',
                      align: 'right' as const,
                      render: (value: number) => formatCurrency(value || 0, 'PLN'),
                    },
                    {
                      title: t('orders:orderDetail.landedCost'),
                      dataIndex: 'effectiveAllocatedLandedCostPLN',
                      key: 'landedCost',
                      align: 'right' as const,
                      render: (value: number) => formatCurrency(value || 0, 'PLN'),
                    },
                    {
                      title: t('orders:orderDetail.caseATotalCost'),
                      dataIndex: 'effectiveAllocatedCostPLN',
                      key: 'effectiveAllocatedCostPLN',
                      align: 'right' as const,
                      render: (value: number) => formatCurrency(value || 0, 'PLN'),
                    },
                  ]}
                />
              ),
            }}
            scroll={{ x: 1120 }}
            columns={[
              {
                title: t('orders:productName'),
                dataIndex: 'name',
                key: 'name',
                render: (_value, record) => getOrderProductDisplayName(record, record.name),
              },
              {
                title: t('orders:quantity'),
                key: 'quantity',
                align: 'right' as const,
                render: (_value, record) => `${record.saleQuantity.toLocaleString('pl-PL')} ${formatUnit(record.saleUnit)}`,
              },
              {
                title: t('orders:orderDetail.revenue'),
                dataIndex: 'revenuePLN',
                key: 'revenue',
                align: 'right' as const,
                render: (value: number) => formatCurrency(value || 0, 'PLN'),
              },
              {
                title: t('orders:orderDetail.purchaseCost'),
                dataIndex: 'supplierPurchaseCostPLN',
                key: 'supplierPurchaseCostPLN',
                align: 'right' as const,
                render: (value: number) => formatCurrency(value || 0, 'PLN'),
              },
              {
                title: t('orders:orderDetail.landedCost'),
                dataIndex: 'supplierLandedCostPLN',
                key: 'supplierLandedCostPLN',
                align: 'right' as const,
                render: (value: number) => formatCurrency(value || 0, 'PLN'),
              },
              {
                title: t('orders:orderDetail.caseATotalCost'),
                dataIndex: 'allocatedCostPLN',
                key: 'allocatedCost',
                align: 'right' as const,
                render: (value: number) => formatCurrency(value || 0, 'PLN'),
              },
              {
                title: t('orders:orderDetail.margin'),
                key: 'margin',
                align: 'right' as const,
                render: (_value, record) => `${formatCurrency(record.marginPLN || 0, 'PLN')} (${formatLocalizedPercent(Number(record.marginPercent || 0), i18n.language)})`,
              },
            ]}
            summary={(data) => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>{t('orders:orderDetail.total')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>{formatCurrency(data.reduce((sum, line) => sum + Number(line.revenuePLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <strong>{formatCurrency(data.reduce((sum, line) => sum + Number(line.supplierPurchaseCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{formatCurrency(data.reduce((sum, line) => sum + Number(line.supplierLandedCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>{formatCurrency(data.reduce((sum, line) => sum + Number(line.allocatedCostPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} align="right">
                  <strong>{formatCurrency(data.reduce((sum, line) => sum + Number(line.marginPLN || 0), 0), 'PLN')}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          {hasCostBuildUp && (
            <div
              style={{
                border: '1px solid #dbe4ee',
                borderRadius: 14,
                background: '#fbfdff',
                padding: 16,
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <div style={{ color: '#0f172a', fontSize: 16, fontWeight: 750 }}>
                  {t('orders:orderDetail.marginBuildUpTitle')}
                </div>
                <div style={{ color: '#64748b', fontSize: 13, marginTop: 3 }}>
                  {t('orders:orderDetail.marginBuildUpDescription')}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', padding: '10px', borderRadius: 10, background: '#ecfdf5' }}>
                  <span style={{ color: '#047857', fontWeight: 750 }}>{t('orders:orderDetail.marginRevenueSection')}</span>
                  <strong style={{ color: '#047857' }}>{formatCurrency(marginExplanation.totals.revenuePLN, 'PLN')}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '8px 10px 2px' }}>
                  <span style={{ color: '#334155', fontWeight: 750 }}>{t('orders:orderDetail.marginCostsSection')}</span>
                  <span style={{ color: '#64748b', fontSize: 12 }}>{t('orders:orderDetail.marginCostsSectionDescription')}</span>
                </div>
                {costBuildUpRows.map((row) => (
                  <div
                    key={row.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 16,
                      alignItems: 'center',
                      padding: '10px',
                      borderRadius: 10,
                      background: row.background,
                    }}
                  >
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                      <span style={{ color: row.color, fontSize: 16, lineHeight: 1.35 }}>{row.icon}</span>
                      <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', color: '#334155', fontWeight: 650 }}>{row.label}</span>
                        <span style={{ display: 'block', color: '#64748b', fontSize: 12, lineHeight: 1.35 }}>{row.description}</span>
                      </span>
                    </div>
                    <strong style={{ color: '#0f172a', whiteSpace: 'nowrap' }}>- {formatCurrency(Math.abs(Number(row.value || 0)), 'PLN')}</strong>
                  </div>
                ))}
              </div>
              <Divider style={{ margin: '14px 0 10px' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' }}>
                <span style={{ color: '#475569', fontWeight: 700 }}>{t('orders:orderDetail.marginTotalCosts')}</span>
                <strong style={{ color: '#0f172a', fontSize: 16 }}>- {formatCurrency(Math.abs(Number(marginExplanation.totals.totalCostPLN || 0)), 'PLN')}</strong>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 16,
                  alignItems: 'center',
                  marginTop: 10,
                  padding: '12px 10px',
                  borderRadius: 10,
                  background: marginExplanation.totals.marginPLN >= 0 ? '#ecfdf5' : '#fef2f2',
                  color: marginExplanation.totals.marginPLN >= 0 ? '#047857' : '#b91c1c',
                }}
              >
                <span style={{ fontWeight: 750 }}>{t('orders:orderDetail.margin')}</span>
                <strong style={{ fontSize: 17 }}>
                  {formatCurrency(marginExplanation.totals.marginPLN, 'PLN')} ({formatLocalizedPercent(marginExplanation.totals.marginPercent, i18n.language)})
                </strong>
              </div>
              {exceptionRows.length > 0 && (
                <div style={{ marginTop: 12, display: 'grid', gap: 4 }}>
                  {exceptionRows.map((row) => (
                    <span key={row.key} style={{ color: '#b45309', fontSize: 12 }}>
                      <WarningOutlined style={{ marginRight: 6 }} />
                      {row.label}: {formatCurrency(row.value, 'PLN')}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </Space>
      ) : (
        <Alert type="info" showIcon message={t('orders:orderDetail.marginExplanationUnavailable')} />
      )}
    </Card>
  );
};
